import type { PortfolioType, TradingStrategyForPortfolio } from '@einhaus/project314-types';
import type {
    StrategySubscriptionPortfolio,
    StrategySubscriptionRow
} from '@/pages/User/PortfolioSettings/components/strategySubscription';

export type StrategySubscriptionGrouping = 'portfolio' | 'strategy';

export type PortfolioTypeFilter = 'all' | PortfolioType;

/** Query parameter holding the grouping, so a grouped view can be bookmarked or linked to. */
export const GROUPING_PARAM = 'groupBy';

export const parseStrategySubscriptionGrouping = (value: string | null): StrategySubscriptionGrouping =>
    value === 'strategy' ? 'strategy' : 'portfolio';

/**
 * Query parameter that reveals inactive strategies. They are hidden by default, so only the opt-in is
 * stored, which keeps the default URL clean.
 */
export const SHOW_INACTIVE_STRATEGIES_PARAM = 'showInactiveStrategies';

/**
 * A switched-off subscription to a globally inactive strategy: nothing can trade and nothing needs
 * fixing, so it is clutter on an overview. Uses the saved switch position rather than an in-flight
 * toggle, so a row does not vanish while its change is still being saved.
 */
export const isInactiveStrategySubscription = (row: StrategySubscriptionRow): boolean => !row.strategy.active && !row.strategy.isActive;

export type PortfolioSubscriptionGroup = {
    kind: 'portfolio';
    key: string;
    portfolio: StrategySubscriptionPortfolio;
    rows: StrategySubscriptionRow[];
};

export type StrategySubscriptionGroupByStrategy = {
    kind: 'strategy';
    key: string;
    strategy: TradingStrategyForPortfolio;
    rows: StrategySubscriptionRow[];
};

export type StrategySubscriptionGroup = PortfolioSubscriptionGroup | StrategySubscriptionGroupByStrategy;

const compareNames = (first: string, second: string): number =>
    first.localeCompare(second, undefined, { numeric: true, sensitivity: 'base' });

/** Live portfolios first, since they trade real money, then alphabetical. */
const comparePortfolios = (first: StrategySubscriptionPortfolio, second: StrategySubscriptionPortfolio): number => {
    if (first.type !== second.type) return first.type === 'live' ? -1 : 1;

    return compareNames(first.name, second.name);
};

/**
 * Groups subscription rows by portfolio or by strategy. Groups and their rows are ordered by name
 * rather than status, so a row never jumps while its switch is being toggled.
 */
export const groupStrategySubscriptionRows = (
    rows: readonly StrategySubscriptionRow[],
    grouping: StrategySubscriptionGrouping
): StrategySubscriptionGroup[] => {
    if (grouping === 'portfolio') {
        const groupsByPortfolioId = new Map<number, PortfolioSubscriptionGroup>();

        for (const row of rows) {
            const group = groupsByPortfolioId.get(row.portfolio.id);

            if (group) {
                group.rows.push(row);
            } else {
                groupsByPortfolioId.set(row.portfolio.id, {
                    kind: 'portfolio',
                    key: `portfolio-${row.portfolio.id}`,
                    portfolio: row.portfolio,
                    rows: [row]
                });
            }
        }

        const groups = [...groupsByPortfolioId.values()].sort((first, second) => comparePortfolios(first.portfolio, second.portfolio));

        for (const group of groups) {
            group.rows.sort((first, second) => compareNames(first.strategy.name, second.strategy.name));
        }

        return groups;
    }

    const groupsByStrategyId = new Map<number, StrategySubscriptionGroupByStrategy>();

    for (const row of rows) {
        const group = groupsByStrategyId.get(row.strategy.id);

        if (group) {
            group.rows.push(row);
        } else {
            groupsByStrategyId.set(row.strategy.id, {
                kind: 'strategy',
                key: `strategy-${row.strategy.id}`,
                strategy: row.strategy,
                rows: [row]
            });
        }
    }

    const groups = [...groupsByStrategyId.values()].sort((first, second) => compareNames(first.strategy.name, second.strategy.name));

    for (const group of groups) {
        group.rows.sort((first, second) => comparePortfolios(first.portfolio, second.portfolio));
    }

    return groups;
};

export const parseSearchTerms = (search: string): string[] => search.trim().toLowerCase().split(/\s+/).filter(Boolean);

/** Every term must appear in the strategy name or id, the portfolio name or id, or the trading profile name. */
export const matchesStrategySubscriptionSearch = (row: StrategySubscriptionRow, terms: readonly string[]): boolean => {
    if (terms.length === 0) return true;

    const haystack =
        `${row.strategy.name} #${row.strategy.id} ${row.portfolio.name} #${row.portfolio.id} ${row.tradingProfileName ?? ''}`.toLowerCase();

    return terms.every((term) => haystack.includes(term));
};

export const matchesPortfolioTypeFilter = (row: StrategySubscriptionRow, filter: PortfolioTypeFilter): boolean =>
    filter === 'all' || row.portfolio.type === filter;

export const buildPortfolioTypeFilterOptions = (
    rows: readonly StrategySubscriptionRow[]
): { value: PortfolioTypeFilter; label: string }[] => {
    let liveCount = 0;

    for (const row of rows) {
        if (row.portfolio.type === 'live') liveCount += 1;
    }

    return [
        { value: 'all', label: `All portfolios (${rows.length.toLocaleString()})` },
        { value: 'live', label: `Live (${liveCount.toLocaleString()})` },
        { value: 'paper', label: `Paper (${(rows.length - liveCount).toLocaleString()})` }
    ];
};

/** Switched on for its portfolio, yet a blocked strategy or incomplete deployment keeps it from opening positions. */
export const isSwitchedOnButNotTrading = (row: StrategySubscriptionRow): boolean =>
    row.isActive && (row.readiness.status === 'blocked' || row.readiness.status === 'actionNeeded');

const pluralize = (count: number, singular: string, plural: string): string =>
    `${count.toLocaleString()} ${count === 1 ? singular : plural}`;

export const describeStrategySubscriptionGroup = (group: StrategySubscriptionGroup): string => {
    const runningCount = group.rows.filter((row) => row.readiness.status === 'live').length;

    const size =
        group.kind === 'portfolio'
            ? pluralize(group.rows.length, 'strategy', 'strategies')
            : `In ${pluralize(group.rows.length, 'portfolio', 'portfolios')}`;

    return `${size} · ${runningCount.toLocaleString()} running`;
};

export const describeSubscriptionReach = (rows: readonly StrategySubscriptionRow[]): string => {
    const portfolioIds = new Set<number>();
    const strategyIds = new Set<number>();

    for (const row of rows) {
        portfolioIds.add(row.portfolio.id);
        strategyIds.add(row.strategy.id);
    }

    return `${pluralize(strategyIds.size, 'strategy', 'strategies')} across ${pluralize(portfolioIds.size, 'portfolio', 'portfolios')}`;
};
